<template>
  <component
    :is="tag"
    class="app-card"
    :class="[
      `app-card--padding-${padding}`,
      `app-card--variant-${variant}`,
      { 'app-card--new': design === 'new', 'app-card--compact-header': headerDensity === 'compact' }
    ]"
  >
    <header v-if="title || subtitle || $slots.header || $slots.actions" class="app-card__header">
      <div class="app-card__heading">
        <slot name="header">
          <div v-if="title" class="app-card__title-row">
            <component :is="titleTag" class="app-card__title">{{ title }}</component>
            <span v-if="count !== null && variant === 'table'" class="app-card__count">{{ count }}</span>
            <slot name="title-help" />
          </div>
          <p v-if="subtitle" class="app-card__subtitle">{{ subtitle }}</p>
        </slot>
      </div>
      <div v-if="$slots.actions" class="app-card__actions">
        <slot name="actions" />
      </div>
    </header>
    <div class="app-card__body">
      <slot />
    </div>
    <footer v-if="$slots.footer" class="app-card__footer">
      <slot name="footer" />
    </footer>
  </component>
</template>

<script>
export default {
  name: 'AppCard',
  props: {
    tag: {
      type: String,
      default: 'section'
    },
    title: {
      type: String,
      default: ''
    },
    subtitle: {
      type: String,
      default: ''
    },
    count: {
      type: [Number, String],
      default: null
    },
    titleTag: {
      type: String,
      default: 'h2',
      validator: value => ['h2', 'h3', 'h4'].includes(value)
    },
    padding: {
      type: String,
      default: 'normal',
      validator: value => ['none', 'compact', 'normal'].includes(value)
    },
    variant: {
      type: String,
      default: 'default',
      validator: value => ['default', 'panel', 'table'].includes(value)
    },
    headerDensity: {
      type: String,
      default: 'normal',
      validator: value => ['normal', 'compact'].includes(value)
    },
    design: {
      type: String,
      required: true,
      validator: value => value === 'new'
    }
  }
}
</script>

<style lang="scss" scoped>
.app-card--new {
  min-width: 0;
  border: 1px solid $ui-line;
  border-radius: $ui-card-radius;
  background-color: $ui-surface;
  box-shadow: $ui-card-shadow;
  color: $ui-text;
  font-family: $ui-font-body;

  &.app-card--padding-compact {
    padding: $ui-space-16;
  }

  &.app-card--padding-normal {
    padding: $ui-space-20;
  }

  .app-card__header {
    display: flex;
    min-width: 0;
    align-items: flex-start;
    justify-content: space-between;
    gap: $ui-space-16;
    margin-bottom: $ui-space-16;
  }

  .app-card__heading {
    min-width: 0;
  }

  .app-card__title {
    margin: 0;
    color: $ui-ink;
    font-family: $ui-font-heading;
    font-size: $ui-font-size-section-title;
    font-weight: 600;
    line-height: 1.35;
  }

  .app-card__title-row {
    display: flex;
    align-items: center;
    gap: $ui-space-8;
  }

  .app-card__count {
    display: inline-grid;
    min-width: 24px;
    height: 24px;
    padding: 0 $ui-space-4;
    place-items: center;
    border-radius: 12px;
    background: $ui-surface-soft;
    color: $ui-muted;
    font-size: 11px;
    font-weight: 700;
  }

  .app-card__subtitle {
    margin: $ui-space-4 0 0;
    color: $ui-muted;
    font-size: $ui-font-size-body;
    line-height: 1.45;
  }

  .app-card__actions,
  .app-card__footer {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: $ui-space-12;
  }

  .app-card__actions {
    flex: 0 0 auto;
    justify-content: flex-end;
  }

  .app-card__footer {
    justify-content: flex-end;
    margin-top: $ui-space-20;
    padding-top: $ui-space-16;
    border-top: 1px solid $ui-line;
  }

  &.app-card--variant-table {
    padding: 0;
    .app-card__header { align-items: center; margin: 0; padding: $ui-space-20; border-bottom: 1px solid $ui-line; }
    .app-card__heading { flex: 1; }
    &.app-card--compact-header .app-card__header { padding: $ui-space-12 $ui-space-16; }
  }

  &.app-card--variant-panel {
    padding: 0;

    .app-card__header {
      min-height: 52px;
      box-sizing: border-box;
      align-items: center;
      margin-bottom: 0;
      padding: $ui-space-8 $ui-space-16;
      border-bottom: 1px solid $ui-line;
    }

    .app-card__body { padding: $ui-space-16; }
    .app-card__footer { margin: 0; padding: $ui-space-12 $ui-space-16; }
  }
}

@media (max-width: 600px) {
  .app-card--new {
    border-radius: $ui-panel-radius;

    &.app-card--padding-normal:not(.app-card--variant-panel):not(.app-card--variant-table) {
      padding: $ui-space-16;
    }
  }
}
</style>
